import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { getBookkeepingList, lockMonth, unlockMonth, lockAllMonths, unlockAllMonths } from "@/requests/bookkeepping";

import _ from "lodash";
import { FILTER_NAMES } from "@/static/filterNames";
import { Bookkeeping, BookkeepingOfficeProfiles } from "@/models/api/bookkeeping/bookkeeping";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DataTableHeader } from "vuetify";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { bold } from "@/filters/bold";

export interface BookkeepingCloseMonthStateInterface {
  bookkeepingList: Bookkeeping;
  bookkeepingFilters: Array<CriterionBase> | null;
  bookkeepingHeaders: Array<DataTableHeader>;
  recordStampId: number | null;

  isLoading: boolean;
}

export const BookkeepingCloseMonthState: BookkeepingCloseMonthStateInterface = {
  bookkeepingList: new Bookkeeping(),
  bookkeepingFilters: null,
  bookkeepingHeaders: [],
  recordStampId: null,

  isLoading: true,
};

const BookkeepingCloseMonthModule = defineModule({
  state: BookkeepingCloseMonthState as BookkeepingCloseMonthStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    setBookkeepingFilters(state, param: Array<CriterionBase> | null) {
      state.bookkeepingFilters = param;
    },
    setBookkeepingList(state, param: Bookkeeping) {
      state.bookkeepingList = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setHeaders(state, param: BookkeepingOfficeProfiles[]) {
      const headerArray = new Array<DataTableHeader>();
      for (const office of param) {
        headerArray.push({ text: office.name, value: office.id as unknown as string });
      }
      state.bookkeepingHeaders = headerArray;
    },
    setRecordStampDialog(state, param: number | null) {
      state.recordStampId = param;
    },
  },
  actions: {
    async downloadList(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const bookkeeping = await getBookkeepingList({
        count: 12,
        page: 0,
        criterions: state.bookkeepingFilters === null ? null : _.cloneDeep(state.bookkeepingFilters),
        filterName: FILTER_NAMES.BookkeepingStandardFilter,
      });
      commit.setHeaders(bookkeeping.officeProfiles);
      commit.setBookkeepingList(bookkeeping);
      commit.setIsLoading(false);
    },
    async lockMonth(
      context,
      param: {
        month: number;
        year: number;
        officeProfileId: number | null;
        monthName: string;
        localization: string;
      }
    ) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isLockConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionForMonthInLocalization", {
              action: bold(i18n.t("toLock")),
              month: bold(param.monthName),
              localization: bold(param.localization),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "650px"
        )
      )) as boolean;
      if (isLockConfirmed) {
        await lockMonth(param.month, param.year, param.officeProfileId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadList();
      }
    },
    async unlockMonth(
      context,
      param: {
        month: number;
        year: number;
        officeProfileId: number | null;
        monthName: string;
        localization: string;
      }
    ) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isLockConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionForMonthInLocalization", {
              action: bold(i18n.t("toUnlock")),
              month: bold(param.monthName),
              localization: bold(param.localization),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "650px"
        )
      )) as boolean;
      if (isLockConfirmed) {
        await unlockMonth(param.month, param.year, param.officeProfileId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadList();
      }
    },
    async lockAllMonths(context, param: { month: number; year: number; monthName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isLockConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionForMonthAllLocalization", {
              action: bold(i18n.t("toLock")),
              month: bold(param.monthName),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "650px"
        )
      )) as boolean;
      if (isLockConfirmed) {
        await lockAllMonths(param.month, param.year);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadList();
      }
    },
    async unlockAllMonths(context, param: { month: number; year: number; monthName: string }) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      const isUnlockConfirmed = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(
          filterCapitalize(
            i18n.t("doYouReallyWantToDoActionForMonthAllLocalization", {
              action: bold(i18n.t("toUnlock")),
              month: bold(param.monthName),
            })
          ),
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: filterCapitalize(i18n.t("cancel")),
              color: "warning",
              value: false,
              outlined: true,
            },
            {
              text: filterCapitalize(i18n.t("confirm")),
              color: "warning",
              value: true,
            },
          ],
          "650px"
        )
      )) as boolean;
      if (isUnlockConfirmed) {
        await unlockAllMonths(param.month, param.year);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        dispatch.downloadList();
      }
    },
  },
});

export default BookkeepingCloseMonthModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BookkeepingCloseMonthModule);
